import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

/**
 * Renders markdown content with proper formatting
 */
export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className = '' }) => {
  const components: Components = {
    // Customize heading styles
    h1: ({ children }) => <h1 className="text-2xl font-bold mb-4 mt-6">{children}</h1>,
    h2: ({ children }) => <h2 className="text-xl font-bold mb-3 mt-5">{children}</h2>,
    h3: ({ children }) => <h3 className="text-lg font-bold mb-2 mt-4">{children}</h3>,
    h4: ({ children }) => <h4 className="text-base font-bold mb-2 mt-3">{children}</h4>,
    
    // Customize paragraph
    p: ({ children }) => <p className="mb-3 leading-relaxed">{children}</p>,
    
    // Customize links
    a: ({ children, href }) => (
      <a 
        href={href}
        className="text-blue-600 dark:text-blue-400 hover:underline" 
        target="_blank" 
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
    
    // Customize lists
    ul: ({ children }) => <ul className="list-disc list-outside mb-3 ml-6 pl-0">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal list-outside mb-3 ml-6 pl-0">{children}</ol>,
    li: ({ children }) => <li className="mb-1 ml-0">{children}</li>,
    
    // Customize code blocks
    code: ({ children, className: codeClassName }) => {
      const isInline = !codeClassName;
      return isInline ? (
        <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>
      ) : (
        <code className="block bg-muted p-3 rounded text-sm font-mono overflow-x-auto mb-3">{children}</code>
      );
    },
    
    // Customize pre blocks
    pre: ({ children }) => <pre className="bg-muted p-3 rounded overflow-x-auto mb-3">{children}</pre>,
    
    // Customize blockquotes
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-muted-foreground/30 pl-4 italic my-3">{children}</blockquote>
    ),
    
    // Customize strong/bold
    strong: ({ children }) => <strong className="font-bold">{children}</strong>,
    
    // Customize emphasis/italic
    em: ({ children }) => <em className="italic">{children}</em>,
    
    // Customize horizontal rule
    hr: () => <hr className="my-4 border-border" />,
  };

  return (
    <div className={`markdown-content ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
